import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
  DOCUMENT,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AppData } from 'src/app/data';
import {
  FormControl,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { NotificationService, TranslateService } from 'src/app/services';
import { environment } from 'src/environments/environment';
import { LoadingController, ModalController, IonicModule } from '@ionic/angular';
import { filter, firstValueFrom, map, Subject, Subscription, takeUntil, tap } from 'rxjs';
import { PaymentsService } from 'src/app/services/payments.service';
import { BaseJob } from 'src/app/types';
import { DepositFundsResponse } from 'src/app/types/deposit-funds.type';
import { decimalValidator } from 'src/app/validators/decimal.validator';
import { maxNumberValidator, minNumberValidator } from 'src/app/validators/number.validator';
import {
  calculateAdditionalFare,
  calculateJobFare,
  calculateRiderFare,
} from 'src/app/helpers/ui-helpers';
import { NgClass, CurrencyPipe } from '@angular/common';
import { CurrencyComponent } from '../currency/currency.component';
import { FormErrorComponent } from '../form-error/form-error.component';
import { TranslatePipe } from '@ngx-translate/core';

declare const CollectJS: any;

@Component({
  selector: 'app-payments',
  templateUrl: './payments.component.html',
  styleUrls: ['./payments.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    IonicModule,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    CurrencyComponent,
    FormErrorComponent,
    CurrencyPipe,
    TranslatePipe,
  ],
})
export class PaymentsComponent implements OnInit, OnDestroy {
  public baseJob: BaseJob = null;
  private readonly _destroyed$ = new Subject<boolean>();
  public readonly features = environment.features;

  private readonly _renderer = inject(Renderer2);
  private readonly _document = inject(DOCUMENT);
  private readonly _data = inject(AppData);
  public readonly creditCardMin = this._data.route.creditCardMin;
  public readonly creditCardMax = this._data.route.creditCardMax;
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _translateSvc = inject(TranslateService);
  private readonly _modalCtrl = inject(ModalController);
  private readonly _paymentService = inject(PaymentsService);
  private readonly _notificationService = inject(NotificationService);
  private readonly _loadingCtrl = inject(LoadingController);

  public currentStep = 1;
  public scriptLoaded = false;
  public prefersReducedMotion = false;
  private _disconnected$ = this._data.connectionStatus$.pipe(
    map(status => !status.connected || status?.connectionType === 'none'),
    tap(connected => (this.disconnected = connected)),
  );
  public disconnected: boolean;

  public calculateRiderFare = calculateRiderFare;
  public calculateAdditionalFare = calculateAdditionalFare;
  public calculateJobFare = calculateJobFare;

  /** Properties to manage focus styles, and validation errors based on field status provided by CollectJS callback functions */
  public inFocus = { ccnumber: false, ccexp: false, cvv: false };
  public touched = { ccnumber: false, ccexp: false, cvv: false };
  public fieldErrors = { ccnumber: null, ccexp: null, cvv: null };
  private _fieldListeners: { [key: string]: { focus: () => void; blur: () => void } } = {
    ccnumber: { focus: null, blur: null },
    ccexp: { focus: null, blur: null },
    cvv: { focus: null, blur: null },
  };
  public ccIsValid = false;
  private _tokenCallbackTimeout = new Subject<boolean>();

  public readonly form = new FormGroup({
    amount: new FormControl<number>(
      calculateJobFare(this.baseJob) > 2 ? calculateJobFare(this.baseJob) : null,
      [
        Validators.required,
        minNumberValidator(this.creditCardMin),
        maxNumberValidator(this.creditCardMax),
        decimalValidator(2),
      ],
    ),
    ccZip: new FormControl<string>(null, [Validators.required, Validators.pattern(/^[0-9]{5}$/)]),
    sessionId: new FormControl<string>(this._data.sessionId, [Validators.required]),
    paymentToken: new FormControl<string>(null),
    riderId: new FormControl<string | number>(this.baseJob?.riderId, [Validators.required]),
  });
  public readonly zipCodeError = this._translateSvc.translate('VALIDATION.zipCode');
  public submitting = false;

  constructor() {
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this._disconnected$.pipe(takeUntil(this._destroyed$)).subscribe();
  }

  ngOnInit(): void {
    this.form.patchValue({
      riderId: this.baseJob?.riderId,
      amount: Math.max(this._data.route.creditCardMin, calculateJobFare(this.baseJob)),
    });
  }

  private _configureCollectJS(amount: number): Promise<void> {
    const isDark = this._data.isDark;
    const colors = isDark ? environment.colors.dark : environment.colors.light;
    return new Promise(resolve => {
      CollectJS.configure({
        variant: 'inline',
        styleSniffer: 'false',
        placeholderCss: {
          color: isDark ? 'rgba(255,255,255)' : '#131313',
          'font-weight': 'lighter',
          opacity: '.47',
        },
        validCss: {
          color: colors.color,
          'background-color': colors.ionItemBackground,
        },
        invalidCss: { color: colors.color },
        focusCss: { color: colors.color },
        customCss: {
          'background-color': colors.ionItemBackground,
          'font-size': '19.125px',
          'border-left-style': 'hidden',
          'border-right-style': 'hidden',
          'border-top-style': 'hidden',
          'border-bottom-style': 'hidden',
          'padding-top': '8px',
          'padding-bottom': '8px',
        },
        fields: {
          ccnumber: {
            selector: '#ccnumber',
            title: 'Card Number',
            placeholder: '0000 0000 0000 0000',
          },
          ccexp: {
            selector: '#ccexp',
            title: 'Card Expiration',
            placeholder: '00 / 00',
          },
          cvv: {
            display: 'required',
            selector: '#cvv',
            title: 'CVV Code',
            placeholder: '123',
          },
        },
        price: amount.toString(),
        currency: 'USD',
        country: 'US',
        validationCallback: (field: string, valid: boolean, message: string) => {
          if (message === 'Field is empty') {
            message = 'Field is required';
          }
          this.fieldErrors[field] = valid ? null : message;
          this.touched[field] = true;
          this.ccIsValid = Object.values(this.fieldErrors).every(error => !error);
          this._cdr.detectChanges();
        },
        fieldsAvailableCallback: value => {
          const fields = ['ccnumber', 'ccexp', 'cvv'];
          //  add event listeners to each field so we can handle focus styles
          fields.forEach(field => {
            const elem = this._document.getElementById(field);
            this._fieldListeners[field].focus = this._renderer.listen(elem, 'focus', () => {
              this.inFocus[field] = true;
              this._cdr.detectChanges();
            });
            this._fieldListeners[field].focus = this._renderer.listen(elem, 'blur', () => {
              this.inFocus[field] = false;
              this._cdr.detectChanges();
            });
          });
          resolve();
        },
        timeoutDuration: 10000,
        timeoutCallback: () => {
          this._tokenCallbackTimeout.next(true);
        },
        callback: response => {
          this.form.controls.paymentToken.patchValue(response.token);
        },
      });
    });
  }

  public async next({
    increment,
    reset = false,
  }: {
    increment: number
    reset: boolean
  }): Promise<void> {
    this.currentStep = this.currentStep + increment;
    if (reset) {
      this._resetPayment();
    }
    if (this.currentStep === 2) {
      try {
        this.scriptLoaded = false;
        await this._paymentService.loadPayments({
          document: this._document,
          renderer: this._renderer,
        });
        await this._configureCollectJS(this.form.controls.amount.value);
        this.scriptLoaded = true;
      } catch (err) {
        console.error(err);
        this._notificationService.error(this._translateSvc.translate('MESSAGE.genericError'), null);
        this.scriptLoaded = false;
      }
    }
  }

  /** Clean up event listeners added to the nmi fields so we don't cause memory leaks */
  private _destroyListeners() {
    const fields = ['ccnumber', 'ccexp', 'cvv'];
    fields.forEach(fields => {
      if (this._fieldListeners[fields].focus) {
        this._fieldListeners[fields]?.focus();
        this._fieldListeners[fields].focus = null;
      }
      if (this._fieldListeners[fields].blur) {
        this._fieldListeners[fields]?.blur();
        this._fieldListeners[fields].blur = null;
      }
    });
  }

  private _resetPayment() {
    this._destroyListeners();
    this.inFocus = { ccnumber: false, ccexp: false, cvv: false };
    this.touched = { ccnumber: false, ccexp: false, cvv: false };
    this.fieldErrors = { ccnumber: null, ccexp: null, cvv: null };
  }

  public close(response: DepositFundsResponse | null = null): void {
    this._modalCtrl.dismiss(response, response ? 'submit' : 'cancel');
  }

  public async submitForm() {
    if (this.disconnected) {
      this._notificationService.warning(
        `${this._translateSvc.translate('LABEL.youAreOffline')} ${this._translateSvc.translate('CREDIT_CARDS.offline')}`,
      );
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    let subscription: Subscription;
    const spinner = await this._loadingCtrl.create({
      spinner: 'crescent',
      message: this._translateSvc.translate('CREDIT_CARDS.processing') + '...',
    });
    try {
      spinner.present();
      this.submitting = true;
      //  just in case, clear token field
      const tokenControl = this.form.controls.paymentToken;
      tokenControl.patchValue(null);
      // subscribe in case getting the token request times out so we can throw if it does
      subscription = this._tokenCallbackTimeout.subscribe(value => {
        throw new Error(
          `The tokenization didn't respond in the expected timeframe.  This could be due to an invalid or incomplete field or poor connectivity`,
        );
      });
      // get the token: triggers the CollectJS callback which populates the paymentToken form control
      CollectJS.startPaymentRequest();
      //  wait for the callback to populate the form with the token value
      await firstValueFrom(tokenControl.valueChanges.pipe(filter(value => !!value)));
      //  update balance
      const { riderId, ...props } = this.form.getRawValue();
      const payload = { ...props, riderId: riderId };
      const response = await this._paymentService.updateBalance(payload);
      this._notificationService.success(
        `${this._translateSvc.translate('CREDIT_CARDS.successUpdateBalance')} ${response.currentBalance.toLocaleString()}`,
      );
      this.close(response);
    } catch (err) {
      console.error(err);
      this._notificationService.error(err?.error?.message || err?.message || err);
    } finally {
      subscription.unsubscribe();
      this.submitting = false;
      spinner.dismiss();
    }
  }

  ngOnDestroy(): void {
    this._destroyListeners();
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }
}
